import { useState } from 'react';
import { SigninUserInput } from '../../../../../graphql.generated';
import {
    SigninUserDocument,
    SigninUserMutation,
    SigninUserMutationVariables
} from '../queries/signIn.generated';
import { triggerSuccessfulAuthentication } from '../../../utils/triggerSuccessfulAuthentication';
import { logError } from '@zoopla/logger';
import { useLazyFetchGraphql } from '@zoopla/fetch-graphql';
import {
    FetchUserWithTrackedPropertiesDocument,
    FetchUserWithTrackedPropertiesQuery
} from '../queries/userWithTrackedProperties.generated';
import { setActiveSession } from '@zoopla/user';

export type SignInUserPayload = {
    success: boolean;
    errorMessage: string | null;
    errorCode: string | null;
    errorField: string | null;
};

export const useSignIn = ({ onSuccess }: { onSuccess: () => void }) => {
    const [generalError, setGeneralError] = useState('');

    // this function is used to fetch the user after the signin has succeded
    // by the components using it
    const { lazyFetch: getUserData } =
        useLazyFetchGraphql<FetchUserWithTrackedPropertiesQuery>(
            FetchUserWithTrackedPropertiesDocument
        );

    const { lazyFetch: signInMutation } = useLazyFetchGraphql<
        SigninUserMutation,
        SigninUserMutationVariables
    >(SigninUserDocument, {
        onCompleted({ signinUser }) {
            if (!signinUser?.success && signinUser?.errorCode === 'PASSWORD') {
                setGeneralError(
                    'Incorrect email or password. Please check and try again'
                );
                return;
            }

            if (!signinUser?.success) {
                setGeneralError(
                    'Sorry, something went wrong. Please try again.'
                );
                return;
            }
            setActiveSession();
            triggerSuccessfulAuthentication();
            onSuccess();
        },
        onError(error) {
            logError(error);
            setGeneralError('Sorry, something went wrong. Please try again.');
        }
    });

    const signInUser = ({ email, password, rememberMe }: SigninUserInput) => {
        setGeneralError('');

        return signInMutation({
            input: {
                email,
                password,
                rememberMe
            }
        });
    };

    const resetGeneralError = () => {
        setGeneralError('');
    };

    return {
        generalError,
        signInUser,
        resetGeneralError,
        getUserData
    };
};
